import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { brand, fontFamily, tg } from '../../theme/tokens';
import type { Scene } from '../../data/scenes';

interface SceneCarouselProps {
  scenes: Scene[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

/**
 * Horizontal scene picker — large "active" card on the left, smaller peek
 * cards on the right (scaled + desaturated), with native swipe via Embla.
 *
 * Design choices vs. v1 grid:
 *   - One card in focus at a time so example previews below have a clear
 *     anchor scene ("Examples from X")
 *   - Peek cards are touch targets — tap snaps them to active
 *   - Forward-only peek (no left-side peek) for simpler mental model
 *   - Pagination dots double as a visual scrollbar
 *
 * Embla gives us robust pointer/wheel handling and a stable `selectedIndex`
 * API; we drive `selectedId` upward so the rest of the page (examples,
 * CTA label) reacts to selection.
 */
export function SceneCarousel({ scenes, selectedId, onSelect }: SceneCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Sync Embla → React state and bubble selection up via id.
  useEffect(() => {
    if (!emblaApi) return;
    const onSelectScroll = () => {
      const idx = emblaApi.selectedScrollSnap();
      setSelectedIndex(idx);
      const scene = scenes[idx];
      if (scene && scene.id !== selectedId) onSelect(scene.id);
    };
    emblaApi.on('select', onSelectScroll);
    onSelectScroll(); // fire once on mount to align state
    return () => {
      emblaApi.off('select', onSelectScroll);
    };
  }, [emblaApi, scenes, selectedId, onSelect]);

  // External selection (e.g. tapping a dot, or restoring saved state) →
  // scroll Embla to that index. Guard against re-firing during user drag
  // which would cause a fight between mouse and programmatic scroll.
  useEffect(() => {
    if (!emblaApi || !selectedId) return;
    const idx = scenes.findIndex((s) => s.id === selectedId);
    if (idx >= 0 && idx !== emblaApi.selectedScrollSnap()) {
      emblaApi.scrollTo(idx);
    }
  }, [emblaApi, scenes, selectedId]);

  const scrollTo = useCallback((idx: number) => emblaApi?.scrollTo(idx), [emblaApi]);

  return (
    <div style={{ marginTop: 12, fontFamily }}>
      <div ref={emblaRef} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 14, padding: '10px 20px', alignItems: 'center' }}>
          {scenes.map((s, i) => {
            const isActive = i === selectedIndex;
            // Only the card immediately to the right of active shows a
            // chevron tap-hint — telling user "tap me to bring forward".
            // Showing it on every peek looked cluttered in the mockup.
            const showTapHint = i === selectedIndex + 1;
            return (
              <SceneCard
                key={s.id}
                scene={s}
                active={isActive}
                showTapHint={showTapHint}
                onTap={() => scrollTo(i)}
              />
            );
          })}
        </div>
      </div>

      {/* Pagination dots — visual progress, also taps to jump. The active
          dot stretches into a pill so it stands out at a glance. */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 8 }}>
        {scenes.map((s, i) => (
          <button
            key={s.id}
            onClick={() => scrollTo(i)}
            aria-label={`Go to scene ${s.name}`}
            style={{
              appearance: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              width: i === selectedIndex ? 18 : 6,
              height: 6,
              borderRadius: 999,
              background: i === selectedIndex ? brand.solid : tg.divider,
              transition: 'width 200ms, background 200ms',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
function SceneCard({
  scene,
  active,
  showTapHint,
  onTap,
}: {
  scene: Scene;
  active: boolean;
  showTapHint: boolean;
  onTap: () => void;
}) {
  return (
    <div
      onClick={onTap}
      style={{
        flex: '0 0 240px',
        aspectRatio: '3 / 4',
        borderRadius: 22,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        background: scene.gradient,
        // Peek-vs-active styling: scale down and desaturate inactive cards
        // so the focus card unambiguously wins attention. transform-origin
        // anchored to the left so peek cards collapse INWARD (toward the
        // active card), not centered — that's what gives the staggered
        // overlapping look from the mockup.
        transform: active ? 'scale(1)' : 'scale(0.7)',
        transformOrigin: 'left center',
        opacity: active ? 1 : 0.6,
        filter: active ? 'none' : 'saturate(0.45) brightness(0.55)',
        boxShadow: active
          ? `0 18px 40px -10px rgba(0,0,0,0.6), 0 0 0 3px ${brand.solid}, 0 0 0 6px rgba(229,64,122,0.3)`
          : 'none',
        transition: 'transform 300ms ease, opacity 300ms ease, filter 300ms ease, box-shadow 300ms ease',
      }}
    >
      {/* Heart badge — confirms current selection visually on the active card */}
      {active && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: 999,
            background: brand.solid,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${brand.solid}88`,
          }}
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="#fff">
            <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z" />
          </svg>
        </div>
      )}

      {/* Tap-hint chevron — only on the card immediately right of active. */}
      {showTapHint && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            right: 8,
            transform: 'translateY(-50%)',
            width: 28,
            height: 28,
            borderRadius: 999,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            pointerEvents: 'none',
            opacity: 0.9,
          }}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </div>
      )}

      {/* Two-line label bottom-left — name + tagline */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          right: 16,
          color: '#fff',
          fontSize: 18,
          fontWeight: 800,
          letterSpacing: -0.3,
          textShadow: '0 2px 6px rgba(0,0,0,0.5)',
        }}
      >
        {scene.name}
        <span
          style={{
            display: 'block',
            fontSize: 12,
            fontWeight: 500,
            opacity: 0.85,
            marginTop: 4,
            letterSpacing: 0.2,
          }}
        >
          {scene.tagline}
        </span>
      </div>
    </div>
  );
}
